import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { Temporal } from '@imago/api-client'
import { API_Care_Subscribe_Response, API_Care_Terminate_Response, CareInfo, CareInfoWeak, deserializeCareInfo, InvoiceInfo } from '@imago/model'
import { api } from "./_imago_api.ts"
import { FatUserQuery } from './user.ts'
import { useGAHTConsented } from './user-emrs.ts'
import { Zoned } from './_zoned.ts'

export const CareQuery = {
	use() {
		return {data: FatUserQuery.use().data?.care}
	},
	async setData(qc: QueryClient, k: [], care: CareInfo) {
		await FatUserQuery.patchData(qc, [], old => ({
			...old!,
			care,
		}))
	},
	async invalidate(qc: QueryClient, k: []) {
		await FatUserQuery.invalidate(qc, [])
	},
}

type FatCareInfoWeak = CareInfoWeak & {
	started: Temporal.Instant | null
}

export type FatCareInfo = CareInfo & {
	started: Temporal.Instant | null
}

export function _zoneInvoice<T extends InvoiceInfo>(invoice: T): Zoned<T> {
	const tz = Temporal.Now.timeZoneId()
	return {
		ID: invoice.ID,
		number: invoice.number,
		periodStart: invoice.periodStart.toZonedDateTimeISO(tz),
		periodEnd: invoice.periodEnd.toZonedDateTimeISO(tz),
		amountRemaining: invoice.amountRemaining,
		dueBy: invoice.dueBy.toZonedDateTimeISO(tz),
		nextAttemptOn: invoice.nextAttemptOn?.toZonedDateTimeISO(tz) ?? null,
	} satisfies Zoned<InvoiceInfo> as any
}

export function _zoneCare<T extends FatCareInfo>(care: T): Zoned<T> {
	const tz = Temporal.Now.timeZoneId()
	return {
		started: care.started?.toZonedDateTimeISO(tz) ?? null,
		terminated: care.terminated?.toZonedDateTimeISO(tz) ?? null,
		nextCharge: care.nextCharge?.toZonedDateTimeISO(tz) ?? null,
		unpaidInvoices: care.unpaidInvoices.map(_zoneInvoice),
	} satisfies Zoned<FatCareInfoWeak> as any
}

export function useCare(): Zoned<FatCareInfo> | undefined {
	const care = CareQuery.use().data
	const started = useGAHTConsented()
	if (care === undefined || started === undefined) return
	return _zoneCare({
		...care,
		started,
	})
}

export function _useSubscribe() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async () => {
			try {
				const data = await api.post('care/').json() as API_Care_Subscribe_Response
				CareQuery.setData(qc, [], deserializeCareInfo(data.care))
				return {intent: data.intent}
			} catch (e) {
				CareQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return mutation
}

export function _useTerminateCare() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async () => {
			try {
				const data = await api.delete('care/').json() as API_Care_Terminate_Response
				CareQuery.setData(qc, [], deserializeCareInfo(data.care))
			} catch (e) {
				CareQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return mutation
}
